import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';

// Free Spin (Lucky Spin) wheel component (JavaScript version)
// Props:
// - apiBaseUrl: string (e.g., https://api2b.pro)
// - token?: string | null
// - logoUrl?: string | null
// - prizes?: string[]
// - restricted?: Set<string> (labels to exclude from selection)
// - onResult?: (amount: number) => void

const defaultPrizes = ['৳7','৳14','৳21','৳178','৳28','৳35','৳999','৳9999'];
const defaultRestricted = new Set(['৳178','৳999','৳9999']);

export default function FreeSpin({
  apiBaseUrl,
  token = (typeof window !== 'undefined' ? localStorage.getItem('token') : null),
  logoUrl,
  prizes = defaultPrizes,
  restricted = defaultRestricted,
  onResult,
}) {
  const wheelRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [pendingIndex, setPendingIndex] = useState(null);
  const [wonAmount, setWonAmount] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [errorAlreadyClaimed, setErrorAlreadyClaimed] = useState(false);
  const [userData, setUserData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [eligibilityMsg, setEligibilityMsg] = useState('');
  const [showEligibilityMsg, setShowEligibilityMsg] = useState(false);

  const sliceCount = prizes.length;
  const sliceAngle = 360 / sliceCount;

  const allowedIndexes = useMemo(() => (
    prizes
      .map((p, i) => (restricted.has(p) ? -1 : i))
      .filter((i) => i !== -1)
  ), [prizes, restricted]);

  const toDate = (input) => {
    if (!input) return null;
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const isSameDay = (a, b) => (
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );

  const isSuccessTx = (t) => {
    const status = String(t?.status ?? t?.Status ?? '').toLowerCase();
    const key = String(t?.key ?? '').toLowerCase();
    return status === 'success' || status === 'successful' || key === '1';
  };

  const isDepositTx = (t) => String(t?.action || '').toLowerCase() === 'deposit';

  const isFreeSpinTx = (t) => {
    const action = String(t?.action || '').toLowerCase();
    return action === 'freespin'
      || action === 'free_spin'
      || action === 'free spin'
      || action === 'dailyspin'
      || action === 'daily_spain'
      || action === 'daily-spain';
  };

  const openEligibilityMsg = useCallback((msg) => {
    setEligibilityMsg(msg);
    setShowEligibilityMsg(true);
  }, []);

  const fetchLatestGuardData = useCallback(async () => {
    if (!token) {
      setUserData(null);
      setTransactions([]);
      return { user: null, txs: [] };
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const rid = (typeof window !== 'undefined') ? (localStorage.getItem('remote_id') || localStorage.getItem('user_id') || localStorage.getItem('id') || '') : '';
      const userUrl = rid ? `${apiBaseUrl}/api/user/show?remote_id=${encodeURIComponent(rid)}` : `${apiBaseUrl}/api/user/show`;
      const txUrl = rid ? `${apiBaseUrl}/api/transaction/show?remote_id=${encodeURIComponent(rid)}` : `${apiBaseUrl}/api/transaction/show`;

      const [userRes, txRes] = await Promise.all([
        fetch(userUrl, { headers }),
        fetch(txUrl, { headers }),
      ]);

      const user = userRes.ok ? await userRes.json() : null;
      const txJson = txRes.ok ? await txRes.json() : [];
      const txs = Array.isArray(txJson) ? txJson : (txJson.transactions || []);

      setUserData(user);
      setTransactions(txs);

      return { user, txs };
    } catch (err) {
      console.error('Free spin eligibility fetch failed:', err);
      setUserData(null);
      setTransactions([]);
      return { user: null, txs: [] };
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    if (!token) {
      setUserData(null);
      setTransactions([]);
      return;
    }
    fetchLatestGuardData();
  }, [token, fetchLatestGuardData]);

  const normalize = (angle) => {
    let a = angle % 360;
    if (a < 0) a += 360;
    return a;
  };

  const centerAngleOf = (index) => index * sliceAngle + sliceAngle / 2;

  const spinToIndex = useCallback((targetIndex) => {
    const current = normalize(rotation);
    const center = centerAngleOf(targetIndex);
    const margin = Math.max(2, Math.min(8, sliceAngle / 6));
    const epsRange = (sliceAngle / 2) - margin;
    const epsilon = (Math.random() * 2 * epsRange) - epsRange;
    const desired = normalize(360 - (center + epsilon));
    let baseDelta = desired - current;
    if (baseDelta < 0) baseDelta += 360;
    const extraTurns = 5 + Math.floor(Math.random() * 3); // 5..7 turns
    const totalDelta = extraTurns * 360 + baseDelta;

    setSpinning(true);
    setPendingIndex(targetIndex);
    setShowResult(false);
    setRotation((prev) => prev + totalDelta);
  }, [rotation, sliceAngle]);

  const spin = useCallback(async () => {
    if (spinning || allowedIndexes.length === 0) return;

    // Rule 1: login required
    if (!token) {
      openEligibilityMsg('You must be logged in to spin.');
      return;
    }

    // Always pull fresh user + transaction data before enforcing rules
    const { user, txs } = await fetchLatestGuardData();
    const userInfo = user || userData;
    const txList = (txs && txs.length) ? txs : transactions;

    // Rule 2: phone verification via server userData
    const phoneVerified = userInfo && userInfo.session_id === '1';
    if (!phoneVerified) {
      openEligibilityMsg('Please verify your phone number first.');
      return;
    }

    // Rule 3: successful deposit in the last 3 days
    const now = new Date();
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(now.getDate() - 3);

    const hasRecentDeposit = (txList || []).some((t) => {
      const created = toDate(t?.created_at);
      return created && created >= threeDaysAgo && isDepositTx(t) && isSuccessTx(t);
    });

    if (!hasRecentDeposit) {
      openEligibilityMsg('You must deposit successfully in the last 3 days to spin.');
      return;
    }

    // Rule 4: one free spin per day (server transactions)
    const claimedToday = (txList || []).some((t) => {
      const created = toDate(t?.created_at);
      return created && isSameDay(created, now) && isFreeSpinTx(t) && isSuccessTx(t);
    });

    if (claimedToday) {
      openEligibilityMsg('You have already claimed your free spin for today!');
      return;
    }

    const targetIndex = allowedIndexes[Math.floor(Math.random() * allowedIndexes.length)];
    spinToIndex(targetIndex);
  }, [spinning, allowedIndexes, spinToIndex, token, fetchLatestGuardData, openEligibilityMsg, userData, transactions]);

  const onTransitionEnd = async (ev) => {
    if (ev.propertyName !== 'transform' || !spinning) return;
    setSpinning(false);

    let idx = pendingIndex;
    let finalAmount = 0;

    if (idx == null) {
      const finalAngle = normalize(rotation);
      const pointerAngle = normalize(360 - finalAngle);
      idx = Math.floor(pointerAngle / sliceAngle);
      const over = pointerAngle - (idx * sliceAngle);
      if (over > sliceAngle / 2 && idx < sliceCount - 1) idx++;
    }

    const amtLabel = prizes[idx];
    setWonAmount(amtLabel);
    setShowResult(true);
    finalAmount = parseInt(String(amtLabel).replace(/[^\d]/g, ''), 10);

    try {
      const res = await fetch(`${apiBaseUrl}/api/free/spin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ amount: finalAmount }),
      });

      if (res.status === 400) {
        setErrorAlreadyClaimed(true);
        return;
      }
      if (!res.ok) {
        console.error('Free spin failed:', res.status);
        return;
      }
      if (onResult) onResult(finalAmount);
    } catch (err) {
      console.error('Free spin network error:', err);
    }
  };

  return (
    <div className="fs-overlay">
      <div className="fs-modal">
        <div className="fs-wheel-container">
          <div className="fs-pointer" />
          <div
            ref={wheelRef}
            className="fs-wheel"
            style={{ transform: `rotate(${rotation}deg)` }}
            onTransitionEnd={onTransitionEnd}
          >
            {prizes.map((p, i) => (
              <div
                className="fs-slice-number"
                key={i}
                style={{ '--angle': `${i * sliceAngle + sliceAngle / 2}deg` }}
              >
                {p}
              </div>
            ))}
            <div className="fs-center-circle">
              {logoUrl ? (
                <img src={logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button disabled={spinning} onClick={spin} className="fs-spin-btn">
            {spinning ? 'Spinning…' : 'SPIN'}
          </button>
        </div>

        {showResult && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              🎉 Congratulations!<br />
              You won <span className="fs-amount">{wonAmount}</span><br />
              <button className="fs-close-btn" onClick={() => setShowResult(false)}>OK</button>
            </div>
          </div>
        )}

        {errorAlreadyClaimed && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              ❌<br />
              You have already claimed your free spin for today!<br />
              <button className="fs-close-btn" onClick={() => setErrorAlreadyClaimed(false)}>OK</button>
            </div>
          </div>
        )}

        {showEligibilityMsg && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              ℹ️<br />
              {eligibilityMsg}<br />
              <button className="fs-close-btn" onClick={() => setShowEligibilityMsg(false)}>OK</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
